"use client";

import { useEffect, useMemo, useState } from "react";
import {
  BedDouble,
  CalendarDays,
  MapPinned,
  Plane,
  Sparkles,
  Tag,
} from "lucide-react";
import { iHolidayQuoteX } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { partitionContentForTripMode } from "./partition-content-for-trip-mode";
import { partitionItineraryForTripMode } from "./partition-itinerary-for-trip-mode";
import {
  quoteThemeColorStyles,
  quoteThemeCssVars,
} from "../quote-theme/quote-theme-css-vars";
import { cn } from "@/lib/utils";

type TripSectionId =
  | "trip-section-story"
  | "trip-section-days"
  | "trip-section-stays"
  | "trip-section-flights"
  | "trip-section-experiences"
  | "trip-section-more"
  | "trip-section-pricing";

interface NavItem {
  id: TripSectionId;
  label: string;
  icon: typeof CalendarDays;
  targetSelector?: string;
}

export function TripModeSectionNav({ quote }: { quote: iHolidayQuoteX }) {
  const items = useMemo(() => {
    const {
      dayByDayBlocks,
      preItineraryBlocks,
      pricingBlocks,
      postItineraryBlocks,
      postPricingBlocks,
    } = partitionContentForTripMode(quote);
    const { hotels, flights, other } = partitionItineraryForTripMode(
      quote.itineraryBlocks || []
    );

    const next: NavItem[] = [];
    if (preItineraryBlocks.some((block) => block.type !== "day_by_day_itinerary")) {
      next.push({
        id: "trip-section-story",
        label: "Overview",
        icon: Sparkles,
      });
    }
    if (dayByDayBlocks.length > 0) {
      next.push({
        id: "trip-section-days",
        label: "Days",
        icon: CalendarDays,
        targetSelector: '[id^="trip-section-days"]',
      });
    }
    if (hotels.length > 0) {
      next.push({
        id: "trip-section-stays",
        label: "Stays",
        icon: BedDouble,
      });
    }
    if (flights.length > 0) {
      next.push({
        id: "trip-section-flights",
        label: "Flights",
        icon: Plane,
      });
    }
    if (other.length > 0) {
      next.push({
        id: "trip-section-experiences",
        label: "Extras",
        icon: MapPinned,
      });
    }
    if (postItineraryBlocks.length > 0) {
      next.push({
        id: "trip-section-more",
        label: "More",
        icon: Sparkles,
      });
    }
    if (pricingBlocks.length > 0 || postPricingBlocks.length > 0) {
      next.push({
        id: "trip-section-pricing",
        label: "Pricing",
        icon: Tag,
      });
    }
    return next;
  }, [quote]);

  const [activeId, setActiveId] = useState<TripSectionId | null>(
    items[0]?.id ?? null
  );

  useEffect(() => {
    if (items.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) =>
              (b.intersectionRatio || 0) - (a.intersectionRatio || 0)
          );
        const targetId = visible[0]?.target?.id;
        if (targetId) {
          const activeItem = items.find((item) =>
            item.targetSelector
              ? targetId.startsWith(item.id)
              : targetId === item.id
          );
          if (activeItem) setActiveId(activeItem.id);
        }
      },
      {
        rootMargin: "-20% 0px -55% 0px",
        threshold: [0.1, 0.25, 0.5],
      }
    );

    for (const item of items) {
      const elements = item.targetSelector
        ? document.querySelectorAll<HTMLElement>(item.targetSelector)
        : document.getElementById(item.id)
          ? [document.getElementById(item.id) as HTMLElement]
          : [];
      for (const el of elements) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [items]);

  if (items.length < 2) return null;

  return (
    <nav
      aria-label="Trip sections"
      className="sticky top-0 z-40 -mx-4 mb-2 border-b bg-white/90 px-4 py-2 backdrop-blur sm:-mx-0 sm:rounded-2xl sm:border sm:shadow-sm"
      style={{ borderColor: quoteThemeCssVars.primary20 }}
    >
      <ul className="flex gap-1.5 overflow-x-auto [-webkit-overflow-scrolling:touch] sm:justify-center">
        {items.map((item) => {
          const Icon = item.icon;
          const active = item.id === activeId;
          return (
            <li key={item.id} className="shrink-0">
              <button
                type="button"
                onClick={() => {
                  setActiveId(item.id);
                  const target = item.targetSelector
                    ? document.querySelector<HTMLElement>(item.targetSelector)
                    : document.getElementById(item.id);
                  target?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors sm:text-sm",
                  active ? "text-white" : "text-gray-700 hover:bg-gray-50"
                )}
                style={
                  active
                    ? quoteThemeColorStyles.primaryButton
                    : {
                        backgroundColor: quoteThemeCssVars.primary10,
                        color: quoteThemeCssVars.primary,
                      }
                }
              >
                <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                {item.label}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
